'use client';

import React, { FC, useEffect, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { Document } from '@contentful/rich-text-types';
import HeaderText, { HeaderSize } from '@/components/ui/HeaderText/HeaderText';

import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  DynamicComponent,
  Errors,
  RichText,
  Select,
  TrustedShopReviews,
} from '@/components';

import LineAndBlock from '@/components/checkout/Skeletons/LineAndBlock';

import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import ProductValueProps from '@/components/common/ProductAddToCart/ProductValueProps';
import { VariantSwitcher } from '@/components/common/ProductAddToCart/VariantSwitcher';

import {
  buildAddonProductProperties,
  buildPrimaryProductProperties,
} from '@/components/common/ProductAddToCart/utils';

import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';

import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import useProductContext from '@/context/ProductContext';

import { ERROR_KINDS, ITEM_ORIGINS } from '@/core/constants';
import { ExtendedProductFields } from '@/core/contentfulConfig';
import { FeatureFlag } from '@/core/featureFlags';
import { TypeProduct, TypeProductFields } from '@/core/generated-types';

import useLocale from '@/hooks/useLocale';
import { usePlayGeniusCheckout } from '@/hooks/usePlayGeniusCheckout/usePlayGeniusCheckout';
import { PlayGeniusCheckoutSource } from '@/hooks/usePlayGeniusCheckout/usePlayGeniusCheckout';
import { useProductValidations } from '@/hooks/useProductValidations';

import { API_URLS } from '@/lib/api/config';
import { TypeSubscriptionPlanInstallmentsResponse } from '@/lib/api/subscriptionManager/types';

import {
  getProductInventoryStatus,
  ShopifyProduct,
  ShopifyProductType,
  ShopifyVariant,
} from '@/lib/shopify';

import { formatMoney } from '@/lib/shopify/utils';

import { usePathname } from '@/navigation';

import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';

import {
  shouldUseTrustedShopsReviews,
  shouldUseYotpoReviews,
} from '@/utils/helpers';

import { AddButton, InventoryStatus } from '../InventoryStatus';
import { ProductAddOns } from '../ProductAddOns';

import {
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_EVENT_TYPES,
} from '@/utils/rudderstack';

import { getDefaultVariant } from '@/utils/shopify';
import { handleCartFormSubmit } from '@/utils/shopify/handleCartFormSubmit';

import { TariffPDPMessage } from '../Tariff';

import { TypeTariffSettings } from '../types';
import AddToCartErrorMessage from '../AddToCartErrorMessage/AddToCartErrorMessage';

export interface ProductAddToCartProps {
  shopifyProduct: ShopifyProduct | null;
  product: TypeProduct;
  isVariantSwitcherDisplayed: boolean;
  subscriptionPlanInstallments?: TypeSubscriptionPlanInstallmentsResponse;
  tariffSettings?: TypeTariffSettings;
}

export interface SelectedAddOns {
  fields: TypeProductFields;
  variant: ShopifyVariant;
}

const ProductAddToCart: FC<ProductAddToCartProps> = ({
  shopifyProduct,
  product,
  isVariantSwitcherDisplayed,
  subscriptionPlanInstallments,
  tariffSettings,
}) => {
  const locale = useLocale();

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const pathname = usePathname();

  const searchParams = useSearchParams();

  const variantUrlParams = searchParams?.get('variant');

  const {
    enableYotpoReviewsSummary,
    enableTrustedShopsReviewsSummary,
    productVariantsCopy,
  } = useProductContext();

  const productFields = product.fields;

  const { variants } = shopifyProduct as ShopifyProduct;

  const defaultVariant = getDefaultVariant(productFields.title, variants);

  const [variant, setVariant] = useState<ShopifyVariant>(defaultVariant);

  const [quantity, setQuantity] = useState<string>('1');

  const [selectedAddOns, setSelectedAddOns] = useState<SelectedAddOns[]>([]);

  const [isCheckoutProcessing, setIsCheckoutProcessing] =
    useState<boolean>(false);

  const [addToCartError, setAddToCartError] = useState<string | null>(null);

  const { continueToPlayGeniusCheckout, errorOccurred } = usePlayGeniusCheckout(
    PlayGeniusCheckoutSource.ProductPage,
  );

  const { errorDirectives } = useErrorDirectives();

  const {
    errors: checkoutRequestFailureErrors,
    heading: checkoutRequestFailureHeading,
  } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.FLOW_BLOCKING_API_FAILURE,
  );

  const { errors: coreDataFailureErrors, heading: coreDataFailureHeading } =
    findApplicableErrorDetails(
      errorDirectives,
      ERROR_KINDS.CORE_DATA_DEPENDENCY_FAILURE,
    );

  const qtyVal = 1;

  useEffect(() => {
    if (shopifyProduct && isClient) {
      const urlVariant: ShopifyVariant | undefined = variants?.find(
        (variant) =>
          `gid://shopify/ProductVariant/${variantUrlParams ?? ''}` ==
          variant.id,
      );

      if (urlVariant) {
        setVariant(urlVariant);

        pushEvent('productView', {
          qtyVal,
          shopifyProduct,
          variant: urlVariant,
        });

        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct,
          variant: urlVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });

        return;
      }

      // no variant selected, use the default selected variant
      if (variants && variants.length) {
        setVariant(defaultVariant);

        pushEvent('productView', {
          qtyVal: 1,
          shopifyProduct,
          variant: defaultVariant,
        });

        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct,
          variant: defaultVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });

        return;
      }

      console.warn('Product is missing variants');
    }
  }, [
    shopifyProduct,
    variantUrlParams,
    defaultVariant,
    variants,
    productFields,
    pathname,
    isClient,
  ]);

  const isValidProduct = useProductValidations(product);

  const inventoryStatus = getProductInventoryStatus(shopifyProduct, variant);

  const showValidationMessage =
    productFields.validationMessage && !isValidProduct;

  const isERCSubscriptionProduct =
    shopifyProduct?.productType === ShopifyProductType.EARLY_READER_CLUB;

  const { [FeatureFlag.SUB_SQUAD_ERC_CTA_2146]: ercCtaVariant } = useFlags();

  const isErcCtaVariantB = ercCtaVariant === 'b';

  const shouldRedirectToPlaygeniusCheckout =
    isERCSubscriptionProduct && !isErcCtaVariantB;

  const onSubmitSuccess = () => {
    if (shouldRedirectToPlaygeniusCheckout) {
      continueToPlayGeniusCheckout()
        .catch(console.error)
        .finally(() => setIsCheckoutProcessing(false));
    }
  };

  const selectedAddOnsVariants = selectedAddOns.map((selectedAddOn) => {
    const selectedAddonShopifyProduct = (
      selectedAddOn?.fields as ExtendedProductFields
    )?.shopifyProduct;

    const selectedVariantId = selectedAddOn?.variant.id;
    const defaultVariantId = selectedAddonShopifyProduct?.variants?.[0].id;
    if (selectedAddonShopifyProduct) {
      return {
        merchandiseId: selectedVariantId || defaultVariantId,
        quantity: 1,
        attributes: buildAddonProductProperties(selectedAddonShopifyProduct),
      };
    }
  });

  if (!isClient) {
    // Render a skeleton until client-side data is loaded
    return (
      <div className="sm:px-6">
        <LineAndBlock
          numberOfLines={1}
          numberOfBlocks={2}
          lineClassName="h-32"
          blockClassName="h-12"
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:px-6">
      <div className="flex items-center mb-2 md:mb-3">
        {productFields.previousTitle && (
          <div
            className="mr-2 text-h-x4 font-bold"
            data-testid="previous-title"
          >
            {productFields.previousTitle}
          </div>
        )}

        {productFields.badge && (
          <DynamicComponent component={productFields.badge} />
        )}
      </div>

      <HeaderText
        size={HeaderSize.H1}
        classes="mb-1.5 font-bold text-charcoal-v2"
        text={productFields.title}
        hoverEffect
      />

      {productFields.shortDescription && (
        <div
          className="md:mb-1.5 text-lg md:text-xl"
          data-testid="product-description"
        >
          {productFields.shortDescription}
        </div>
      )}

      {productFields.additionalDescription && (
        <div className="pt-2 md:pt-3 mb-[9px]">
          {productFields.additionalDescription}
        </div>
      )}

      {variant && (
        <div
          className="mb-3 text-[1.3rem] font-bold md:text-2xl"
          data-testid="price"
        >
          {productFields.precedingPriceText && (
            <span data-testid="preceding-price">
              {productFields.precedingPriceText}
            </span>
          )}

          {variant.price?.amount ? formatMoney(variant.price, locale) : null}
        </div>
      )}

      {shouldUseYotpoReviews() && enableYotpoReviewsSummary && (
        <div className="mb-2 md:mb-3" data-testid="yotpo-review-summary">
          <YotpoReviewStars product={product} />
        </div>
      )}

      {shouldUseTrustedShopsReviews() && enableTrustedShopsReviewsSummary && (
        <div className="mb-2" data-testid="trusted-shops-review-summary">
          <TrustedShopReviews
            shopifyProduct={shopifyProduct}
            widgetType="ratings-short"
          />
        </div>
      )}

      {productFields.productAddOns && (
        <ProductAddOns
          productAddOns={productFields.productAddOns}
          selectedAddOns={selectedAddOns}
          setSelectedAddOns={setSelectedAddOns}
          productFields={productFields}
          defaultVariant={defaultVariant}
        />
      )}

      {isVariantSwitcherDisplayed && (
        <VariantSwitcher
          displayVariantType={product?.fields?.displayVariantType || 'tile'}
          selectedVariant={variant}
          variants={shopifyProduct?.variants as ShopifyVariant[]}
          cb={setVariant}
          productVariantsCopy={productVariantsCopy || null}
          product={product}
        />
      )}

      {shopifyProduct && (
        <form
          id="add-to-cart-form"
          data-testid="add-to-cart-form"
          action={`${API_URLS.CART_SERVICE}/add`}
          method="post"
          onSubmit={(e) =>
            handleCartFormSubmit({
              e,
              shopifyProduct,
              shopifyVariant: variant,
              selectedAddOns,
              productFields: product.fields,
              url: pathname,
              defaultQuantity: qtyVal,
              buttonLevel: PRODUCT_ADDED_COMPONENT_TYPES.PRIMARY_CTA,
              onSubmitSuccess,
              onStartFormSubmission: () => {
                setAddToCartError(null);
                shouldRedirectToPlaygeniusCheckout &&
                  setIsCheckoutProcessing(true);
              },
              onSubmitFailure: () => {
                e.preventDefault();
                setAddToCartError(
                  "This item wasn't added to your cart. Please try again.",
                );
              },
              noRedirect: shouldRedirectToPlaygeniusCheckout,
            })
          }
        >
          <input
            type="hidden"
            name="lines"
            value={JSON.stringify([
              // add variants before the main product so that the addon product
              // variants are listed below the primary product in the cart
              ...selectedAddOnsVariants,
              {
                merchandiseId: variant.id,
                quantity: Number(quantity),
                attributes: buildPrimaryProductProperties(
                  shopifyProduct,
                  ITEM_ORIGINS.PRODUCT_PAGE,
                  subscriptionPlanInstallments?.data?.starting_installments,
                  productFields.subscriptionPlanAnchor,
                ),
              },
            ])}
          />

          {/* Hides the quantity selector when the product has a radio option selector */}
          {productFields.quantitySelector && (
            <div className="mb-5 md:mb-6">
              <Select
                {...productFields.quantitySelector}
                name="quantity"
                onChangeEvent={(e) => setQuantity(e.target.value)}
                labelClassName="text-base"
              />
            </div>
          )}

          <div className="text-sm mb-2">
            <InventoryStatus
              inventoryStatus={inventoryStatus}
              product={product}
            />
          </div>

          <AddButton
            inventoryStatus={inventoryStatus}
            product={product}
            isLoading={isCheckoutProcessing}
            isDisabledFromCaller={!subscriptionPlanInstallments?.ok}
            hasAddToCartText={isERCSubscriptionProduct && isErcCtaVariantB}
          />

          {addToCartError && <AddToCartErrorMessage error={addToCartError} />}

          {!subscriptionPlanInstallments?.ok && (
            <Errors
              heading={coreDataFailureHeading}
              errors={coreDataFailureErrors}
              classes="mt-4 md:w-full"
            />
          )}

          {errorOccurred && (
            <Errors
              heading={checkoutRequestFailureHeading}
              errors={checkoutRequestFailureErrors}
              classes="mt-4 md:w-full"
            />
          )}

          {showValidationMessage && (
            <p className="text-base mt-4" data-testid="validation-message">
              <RichText
                openLinkSameWindow={true}
                content={productFields.validationMessage as Document}
              />
            </p>
          )}

          <TariffPDPMessage product={product} tariffSettings={tariffSettings} />
        </form>
      )}

      {!showValidationMessage && (
        <ProductValueProps
          productValueProps={product.fields.productValueProps}
        />
      )}
    </div>
  );
};

export default ProductAddToCart;
